import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Users, Wrench, AlertTriangle, CheckCircle, TrendingUp, Package } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useModulePrices } from '@/hooks/useModulePrices';

interface ClientUsageData {
  client_id: string;
  client_name: string;
  contracted_seats: number;
  used_seats: number;
  active_modules_count: number;
  monthly_cost: number;
  license_status: string;
  billing_cycle: string;
}

export const LicenseUsageDashboard = () => {
  const { modulePrices } = useModulePrices();

  const { data: usageData, isLoading } = useQuery({
    queryKey: ['license-usage-dashboard-modular', modulePrices],
    queryFn: async () => {
      // Fetch active licenses
      const { data: licenses, error } = await supabase
        .from('client_licenses')
        .select(`
          client_id,
          contracted_seats,
          status,
          billing_cycle,
          client:clients(name)
        `)
        .eq('status', 'active');

      if (error) throw error;

      const results: ClientUsageData[] = [];

      for (const license of licenses || []) {
        // Count actual users (seats in use)
        const { data: users } = await supabase
          .from('users')
          .select('id, role, client_id, client_ids')
          .eq('status', 'active');

        const clientUsers = users?.filter(u => {
          if (!['client_admin', 'technician'].includes(u.role)) return false;
          if (u.client_id === license.client_id) return true;
          if (u.client_ids && Array.isArray(u.client_ids)) return u.client_ids.includes(license.client_id);
          return false;
        }) || [];

        // Get active modules
        const { data: modules } = await supabase
          .from('client_modules')
          .select('module_code')
          .eq('client_id', license.client_id)
          .eq('is_active', true);

        const moduleCost = (modules || []).reduce((sum, cm) => {
          const price = modulePrices.find(p => p.code === cm.module_code);
          return sum + (price?.price || 0);
        }, 0);

        const seats = license.contracted_seats || 1;

        results.push({
          client_id: license.client_id,
          client_name: license.client?.name || 'Cliente',
          contracted_seats: seats,
          used_seats: clientUsers.length,
          active_modules_count: modules?.length || 0,
          monthly_cost: moduleCost * seats,
          license_status: license.status || 'active',
          billing_cycle: license.billing_cycle || 'monthly',
        });
      }

      return results;
    },
    refetchInterval: 30000,
    enabled: modulePrices.length > 0,
  });

  const getUsageStatus = (used: number, contracted: number) => {
    if (contracted <= 0) return { status: 'unlimited', percent: 0 };
    const percent = (used / contracted) * 100;
    if (percent >= 100) return { status: 'critical', percent };
    if (percent >= 80) return { status: 'warning', percent };
    return { status: 'healthy', percent };
  };

  const getTotalStats = () => {
    if (!usageData) return { total: 0, critical: 0, warning: 0, healthy: 0 };
    return usageData.reduce((acc, usage) => {
      const s = getUsageStatus(usage.used_seats, usage.contracted_seats);
      acc.total++;
      if (s.percent >= 100) acc.critical++;
      else if (s.percent >= 80) acc.warning++;
      else acc.healthy++;
      return acc;
    }, { total: 0, critical: 0, warning: 0, healthy: 0 });
  };

  if (isLoading) {
    return (
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <Card key={i}>
            <CardHeader className="animate-pulse">
              <div className="h-4 bg-muted rounded w-3/4"></div>
              <div className="h-3 bg-muted rounded w-1/2"></div>
            </CardHeader>
          </Card>
        ))}
      </div>
    );
  }

  const stats = getTotalStats();

  return (
    <div className="space-y-6">
      {/* Cards de resumo */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total de Clientes</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total}</div>
            <p className="text-xs text-muted-foreground">Com assinaturas ativas</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Uso Saudável</CardTitle>
            <CheckCircle className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{stats.healthy}</div>
            <p className="text-xs text-muted-foreground">Abaixo de 80% dos seats</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Próximo do Limite</CardTitle>
            <AlertTriangle className="h-4 w-4 text-yellow-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-600">{stats.warning}</div>
            <p className="text-xs text-muted-foreground">Entre 80% e 100%</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Limite Excedido</CardTitle>
            <TrendingUp className="h-4 w-4 text-red-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">{stats.critical}</div>
            <p className="text-xs text-muted-foreground">100% ou mais</p>
          </CardContent>
        </Card>
      </div>

      {/* Lista detalhada */}
      <Card>
        <CardHeader>
          <CardTitle>Uso de Licenças por Cliente</CardTitle>
          <CardDescription>Seats contratados vs em uso + módulos ativos</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {usageData?.map((usage) => {
              const seatStatus = getUsageStatus(usage.used_seats, usage.contracted_seats);
              return (
                <div key={usage.client_id} className="border rounded-lg p-4">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <h4 className="font-medium">{usage.client_name}</h4>
                      <p className="text-sm text-muted-foreground">
                        {usage.active_modules_count} módulo(s) • R$ {usage.monthly_cost.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}/mês
                      </p>
                    </div>
                    <Badge
                      variant={seatStatus.percent >= 100 ? 'destructive' : seatStatus.percent >= 80 ? 'secondary' : 'default'}
                    >
                      {seatStatus.percent >= 100 ? 'Excedido' : seatStatus.percent >= 80 ? 'Alerta' : 'Normal'}
                    </Badge>
                  </div>

                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium flex items-center gap-1">
                        <Users className="h-4 w-4" />
                        Licenças (Seats)
                      </span>
                      <span className="text-sm">
                        {usage.used_seats} / {usage.contracted_seats}
                      </span>
                    </div>
                    <Progress value={Math.min(seatStatus.percent, 100)} className="h-2" />
                    <p className="text-xs text-muted-foreground">{seatStatus.percent.toFixed(1)}% utilizado</p>
                  </div>
                </div>
              );
            })}

            {(!usageData || usageData.length === 0) && (
              <div className="text-center py-8 text-muted-foreground">
                <Package className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>Nenhuma assinatura ativa encontrada</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
